import { DSLink, DSText } from '@engenharia/ds-react';
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import MetaHeader from '../../components/MetaHeader';

import Captcha from '../../components/Captcha';
import CircularLoader from '../../components/CircularLoader';
import Header from '../../components/Header';
import Notification from '../../components/Notification';
import SelectCountry from '../../components/SelectCountry';
import Tip from '../../components/Tip';
import {
  Card,
  Container,
  ContainerBanner,
  Content,
  EmailSuggest,
  HeaderContent,
  PhoneFieldsContainer,
  SelectCountryField,
  WhatsAppCheckboxContainer,
} from './styles';

import Button from '../../components/atoms/Button';
import BlackFriday from '../../components/Banner/BlackFriday';
import FormHelperTextValidation from '../../components/FormHelperValidation';
import MaskField from '../../components/MaskField';
import { Keys } from '../../constants';
import api from '../../services/api';
import { getLanguage, setAccountType, logout } from '../../services/auth';
import authApi from '../../services/authApi';
import { setDataLayer } from '../../services/gtm';
import { redirectUser } from '../../services/user';
import { allCountries, allCountriesCodeLabels } from '../../utils/countries';
import { getCountryCode, DEFAULT_COUNTRY } from '../../utils/countryCodes';
import { emailSuggestion } from '../../utils/email';
import { kebabize } from '../../utils/kebabize';
import { transcodeLanguage } from '../../utils/language';
import { removeFromLocalStorage, saveToLocalStorage } from '../../utils/localStorage';
import { getUrlParam } from '../../utils/params';
import { sanitizeEmailAddress } from '../../utils/sanitize';
import {
  mailValidation,
  requiredValitidation,
  strongPasswordValidation,
} from '../../utils/validations';
import { countryCode, defaultCountryCode } from '../../config/i18n';

export default function Signup() {
  const { t, i18n } = useTranslation();
  const RefCaptchaSignup = useRef(null);
  const RefCaptchaSignin = useRef(null);

  const params = new URLSearchParams(window.location.search);

  const initialLanguage = getLanguage();
  const [countriesList, setCountriesList] = useState(allCountries(initialLanguage));

  const handleCouponStorage = (coupon) => {
    if (coupon) {
      saveToLocalStorage(Keys.COUPON_CODE, coupon);
    } else {
      removeFromLocalStorage(Keys.COUPON_CODE);
    }
  };

  const [state, setState] = useState(() => {
    const initialState = {
      email: '',
      emailValidation: {},
      name: '',
      nameValidation: false,
      phone: '',
      phoneValidation: {},
      country: defaultCountryCode,
      countryCode: defaultCountryCode,
      whatsappMessagePermission: false,
      password: '',
      signUpPasswordValidation: {},
      showPassword: false,
    };

    const name = params.get('name');
    const email = params.get('email');

    const country = countryCode[getLanguage()] || initialState.country;

    return {
      ...initialState,
      name: name || initialState.name,
      email: email || initialState.email,
      countryCode: country,
      country,
    };
  });

  const [notification, setNotification] = useState({});
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponParams, setCouponParams] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const accountOrigin = getUrlParam('origin') || '';

  const getCookie = (key) => {
    const keyInfo = document.cookie.split('; ').find((row) => row.startsWith(key));

    if (keyInfo) {
      return keyInfo.split('=')[1];
    }

    return undefined;
  };

  const marketingParams = {
    origin: accountOrigin,
    gclid: getCookie('gclid=') || getUrlParam('gclid'),
    utm_source: getCookie('utm_source=') || getUrlParam('utm_source'),
    utm_medium: getCookie('utm_medium=') || getUrlParam('utm_medium'),
    utm_campaign: getCookie('utm_campaign=') || getUrlParam('utm_campaign'),
    origin_page: getCookie('origin_page=') || getUrlParam('origin_page'),
    origin_click: getCookie('origin_click=') || getUrlParam('origin_click'),
  };

  const handleNameChange = (event) => {
    setState({
      ...state,
      name: event.target.value,
      nameValidation: requiredValitidation(event.target.value),
    });
  };

  const handleEmailChange = (event) => {
    const email = sanitizeEmailAddress(event.target.value);
    const suggestion = emailSuggestion(email);

    setState({
      ...state,
      email,
      emailValidation: { ...mailValidation(email), suggestion },
    });
  };

  const isPhoneInvalid = (phone, countryCode = state.countryCode) => {
    const minLength = countryCode === DEFAULT_COUNTRY ? 10 : 5;
    const maxLength = countryCode === DEFAULT_COUNTRY ? 11 : 20;
    return phone && (phone.length < minLength || phone.length > maxLength);
  };

  const setPhoneValidation = (value, countryCode = state.countryCode) => {
    const phoneInvalidError = isPhoneInvalid(value, countryCode);
    return { phoneValidation: { phoneInvalidError } };
  };

  const handlePhoneChange = (event) => {
    setState({
      ...state,
      phone: event.target.value,
      ...setPhoneValidation(event.target.value),
    });
  };

  const handleCountryCodeChange = (currentCountryCode) => {
    setState({
      ...state,
      countryCode: currentCountryCode,
      ...setPhoneValidation(state.phone, currentCountryCode),
    });
  };

  const handleCountryNameChange = (currentCountryIso2) => {
    setState({
      ...state,
      country: currentCountryIso2,
      countryCode: currentCountryIso2,
      ...setPhoneValidation(state.phone, currentCountryIso2),
    });
  };

  const handleWhatsappMessagePermissionChange = () => {
    setState((currentState) => ({
      ...currentState,
      whatsappMessagePermission: !currentState.whatsappMessagePermission,
    }));
  };

  const handleCouponChange = (event) => {
    setCouponParams(event.target.value);
  };

  const handlePasswordChange = (event) => {
    const newValue = event.target.value.replace(' ', '');

    setState({
      ...state,
      password: newValue,
      signUpPasswordValidation: strongPasswordValidation(newValue),
    });
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isLoading) {
      return;
    }

    const validationFields = {
      emailInvalidError: state.emailValidation?.emailInvalidError,
      emailUnconfirmedError: state.emailValidation?.emailUnconfirmedError,
      phoneInvalidError: state.phoneValidation?.phoneInvalidError,
      strongPasswordInvalidError: state.signUpPasswordValidation?.passwordInvalidError,
    };

    const someError = Object.keys(validationFields).find((error) => validationFields[error]);

    if (someError) {
      const translationKey = kebabize(someError)?.replace('-error', '');

      setNotification({
        key: new Date().getTime(),
        message: t(`errors.${translationKey}`),
      });

      return;
    }

    const { email, name, password, country, phone, whatsappMessagePermission, countryCode } = state;
    const language = transcodeLanguage(getLanguage());
    const captcha = await RefCaptchaSignup?.current?.executeAsync();
    const captchaLogin = await RefCaptchaSignin?.current?.executeAsync();

    if (isPhoneInvalid(phone)) return;

    setIsLoading(true);

    try {
      if (couponParams) handleCouponStorage(couponParams);

      await api.post('/accounts', {
        email,
        name,
        password,
        language,
        captcha,
        country,
        phone_code: getCountryCode(countryCode),
        phone_number: phone,
        phone_receive_whatsapp: whatsappMessagePermission,
        ...marketingParams,
      });

      logout();

      const responseLogin = await authApi.post('/accounts/sign_in', {
        email,
        password,
        callback_url: process.env.REACT_APP_FULL_URL,
        language,
        captcha: captchaLogin,
      });

      await redirectUser(responseLogin, true);
    } catch (error) {
      RefCaptchaSignup?.current?.reset();

      setIsLoading(false);

      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  useEffect(() => {
    const coupon = params.get('coupon');

    setDataLayer({
      event: 'begin_register',
    });

    handleCouponStorage(coupon);
    if (coupon) setCouponParams(coupon);

    const handleLanguageChange = (ev) => {
      if (!ev.detail) return;

      const newLanguage = ev.detail.language;

      const languageMap = {
        en: 'en',
        es: 'es',
        pt: 'pt',
      };

      const languageCode = languageMap[newLanguage] || newLanguage;

      setCountriesList(allCountries(languageCode));
    };

    window.addEventListener('ml-translator-on-change-language', handleLanguageChange);

    return () => {
      window.removeEventListener('ml-translator-on-change-language', handleLanguageChange);
    };
  }, []);

  const handleFacebookLogin = async (data) => {
    const { profile, tokenDetail } = data;
    const captcha = await RefCaptchaSignup?.current?.executeAsync();
    const language = transcodeLanguage(getLanguage());

    try {
      const response = await api.post('/accounts/provider_sign_in', {
        captcha,
        language,
        provider: 'facebook',
        user_id: profile.id,
        token: tokenDetail.accessToken,
        name: profile.name,
        email: profile.email,
        ...marketingParams,
      });

      setAccountType('facebook');

      await redirectUser(response, true);
    } catch (error) {
      const { response } = error;

      setNotification({
        key: new Date().getTime(),
        message: response?.data?.message || t('errors.connection-failure'),
      });
    }
  };

  return (
    <>
      <Header positionFixed showLogo />
      <Container>
        <MetaHeader
          title="Cadastro para Teste Grátis - mLabs | Gestão de redes sociais"
          description="Cadastre-se e Teste grátis! Cadastro sem cartão de crédito. Gestão de Redes Sociais é com a mLabs."
        />
        <Content>
          <ContainerBanner>
            <BlackFriday />
          </ContainerBanner>
          <Box mx={2} my={8} component={Card}>
            <div>
              <DSText variant="h3" weight={500} color="primary">
                {t('signup.title')}
              </DSText>
              <HeaderContent>
                <DSText variant="caption" weight={600}>
                  {t('signup.info.card')}
                </DSText>
                <DSText variant="caption">{t('signup.info.spam')}</DSText>
              </HeaderContent>
            </div>
            <form onSubmit={handleSubmit} data-testid="form">
              <FormControl variant="outlined" fullWidth>
                <TextField
                  required
                  type="text"
                  id="name"
                  name="name"
                  data-testid="formName"
                  label={t('signup.form.name')}
                  value={state.name}
                  onChange={handleNameChange}
                  variant="outlined"
                  autoComplete="name"
                  margin="normal"
                  autoFocus
                  error={state.nameValidation}
                  inputProps={{
                    maxLength: 100,
                  }}
                />
                {state.nameValidation && (
                  <FormHelperText error id="name-error">
                    {t('errors.name-invalid')}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  required
                  type="email"
                  id="email"
                  name="email"
                  data-testid="formEmail"
                  label={t('signup.form.email')}
                  value={state.email}
                  onChange={handleEmailChange}
                  variant="outlined"
                  autoComplete="email"
                  margin="normal"
                  error={
                    state.emailValidation?.emailInvalidError ||
                    state.emailValidation?.emailUnconfirmedError
                  }
                />
                {state.emailValidation?.emailInvalidError && (
                  <FormHelperText error id="email-error">
                    {t('errors.email-invalid')}
                  </FormHelperText>
                )}

                {state.emailValidation?.emailUnconfirmedError && (
                  <FormHelperText error id="email-error">
                    {t('errors.email-unconfirmed')}
                  </FormHelperText>
                )}
                {state.emailValidation?.suggestion && (
                  <EmailSuggest variant="caption">
                    {t('errors.email-suggestion')}{' '}
                    <strong>{state.emailValidation?.suggestion}</strong>?
                  </EmailSuggest>
                )}
              </FormControl>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  label={`${t('signup.form.password')}`}
                  variant="outlined"
                  required
                  id="password"
                  name="password"
                  data-testid="formPassword"
                  type={state.showPassword ? 'text' : 'password'}
                  value={state.password}
                  onChange={handlePasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment>
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {state.showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={state.signUpPasswordValidation?.passwordInvalidError}
                />
                {state.password && (
                  <FormHelperTextValidation validation={state.signUpPasswordValidation} />
                )}
              </FormControl>
              <FormControl fullWidth>
                <SelectCountry
                  fullWidth
                  selected={state.country}
                  onSelect={handleCountryNameChange}
                  allCountriesWithCustomLabel={countriesList}
                  searchable
                  searchPlaceholder=" "
                />
              </FormControl>
              <PhoneFieldsContainer fullWidth>
                <SelectCountryField
                  selected={state.countryCode}
                  onSelect={handleCountryCodeChange}
                  allCountriesWithCustomLabel={allCountriesCodeLabels}
                  searchable
                  searchPlaceholder=" "
                  showSecondarySelectedLabel={false}
                  showSecondaryOptionLabel={false}
                />
                <FormControl variant="outlined" fullWidth>
                  <MaskField
                    fullWidth
                    inputMode="numeric"
                    type="text"
                    id="phone"
                    name="phone"
                    data-testid="formPhone"
                    label={t('signup.form.phone')}
                    value={state.phone}
                    onChange={handlePhoneChange}
                    variant="outlined"
                    autoComplete="phone"
                    margin="normal"
                    mask="99999999999999999999"
                    error={state.phoneValidation?.phoneInvalidError}
                    fontSizeLabel={14}
                    fontSizeLegend={11}
                  />
                  {state.phoneValidation?.phoneInvalidError && (
                    <FormHelperText error id="phone-error">
                      {t('errors.phone-invalid')}
                    </FormHelperText>
                  )}
                </FormControl>
              </PhoneFieldsContainer>
              <WhatsAppCheckboxContainer>
                <Checkbox
                  color="secondary"
                  size="medium"
                  margin="none"
                  checked={state.whatsappMessagePermission}
                  onChange={handleWhatsappMessagePermissionChange}
                />
                <DSText variant="caption" weight={400}>
                  {t('signup.form.whatsappMessagePermission')}
                </DSText>
                <Tip informations={`${t('signup.form.tip')} 😊`} />
              </WhatsAppCheckboxContainer>
              {showCouponField ? (
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label={`${t('signup.form.coupon')}`}
                    id="coupon"
                    name="coupon"
                    data-testid="formCoupon"
                    type="text"
                    value={couponParams}
                    onChange={handleCouponChange}
                    variant="outlined"
                    margin="normal"
                    inputProps={{
                      maxLength: 100,
                    }}
                  />
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <DSLink variant="subtitle2" onClick={() => setShowCouponField((value) => !value)}>
                    {t('signup.caption.coupon')}
                  </DSLink>
                </FormControl>
              )}
              <FormControl fullWidth>
                <Button
                  type="submit"
                  id="btnSignup"
                  data-testid="formSubmit"
                  color="primary"
                  fullWidth
                  endIcon={isLoading && <CircularLoader scale="0.4" />}
                >
                  {!isLoading && t('signup.button.start')}
                </Button>
              </FormControl>
            </form>
            <DSText variant="caption" weight={400}>
              {t('signup.caption.title')}
              <DSLink
                variant="caption"
                target="_blank"
                rel="noreferrer"
                href="https://www.mlabs.com.br/politica-de-privacidade"
              >
                {t('signup.caption.privacy')}
              </DSLink>
              {t('signup.caption.and')}
              <DSLink
                variant="caption"
                target="_blank"
                rel="noreferrer"
                href="https://www.mlabs.com.br/termos-de-uso"
              >
                {t('signup.caption.terms')}
              </DSLink>
              {t('signup.caption.dot')}
            </DSText>
          </Box>
          <DSText variant="subtitle2" color="textPrimary">
            {t('signup.caption.subtitle')}
            <DSLink variant="subtitle2" href="/accounts/sign_in">
              {t('signup.caption.action')}
            </DSLink>
          </DSText>
        </Content>
        {notification?.key && (
          <Notification index={notification.key} message={notification.message} />
        )}
        <Captcha ref={RefCaptchaSignup} />
        <Captcha ref={RefCaptchaSignin} size="invisible" />
      </Container>
    </>
  );
}
